// src/pages/SuperAdmin/InstallationRequestsPipeline.tsx - Pipeline Kanban pour les demandes d'installation

import React, { useState, useMemo } from 'react';
import {
  Card,
  Typography,
  Tag,
  Button,
  Empty,
  Spin,
  Input,
  Space,
  Modal,
  Form,
  Badge,
  Tooltip,
  message as antMessage,
  Select,
  Dropdown,
  Menu,
} from 'antd';
import { matchesSearchInFields } from '../../utils/searchUtils';
import {
  PhoneOutlined,
  EnvironmentOutlined,
  MailOutlined,
  WhatsAppOutlined,
  FileTextOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  TagOutlined,
  MoreOutlined,
  EyeOutlined,
  EditOutlined,
  ExportOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import {
  useInstallationRequests,
  useUpdateInstallationRequestStatus,
  useUpdateInstallationRequestNotes,
  useCancelInstallationRequest,
  InstallationRequest,
  InstallationRequestStatus,
} from '../../hooks/useInstallationRequests';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import relativeTime from 'dayjs/plugin/relativeTime';
import './InstallationRequestsPipeline.css';

dayjs.locale('fr');
dayjs.extend(relativeTime);

const { Text, Title } = Typography;
const { Search, TextArea } = Input;

// Configuration des colonnes du pipeline
const PIPELINE_COLUMNS = [
  {
    key: 'pending' as InstallationRequestStatus,
    title: 'Nouveau',
    icon: <ClockCircleOutlined />,
    color: '#faad14',
    bgColor: '#fff7e6',
    description: 'Nouvelles demandes à traiter',
  },
  {
    key: 'contacted' as InstallationRequestStatus,
    title: 'Contacté',
    icon: <PhoneOutlined />,
    color: '#1890ff',
    bgColor: '#e6f7ff',
    description: 'Client contacté, en discussion',
  },
  {
    key: 'installed' as InstallationRequestStatus,
    title: 'Installé',
    icon: <CheckCircleOutlined />,
    color: '#52c41a',
    bgColor: '#f6ffed',
    description: 'Installation réalisée',
  },
  {
    key: 'cancelled' as InstallationRequestStatus,
    title: 'Annulé',
    icon: <CloseCircleOutlined />,
    color: '#ff4d4f',
    bgColor: '#fff1f0',
    description: 'Demande annulée',
  },
];

const forfaitConfig: Record<string, { label: string; color: string; emoji: string }> = {
  SOLO: { label: 'SOLO (1-2 employés)', color: 'blue', emoji: '👤' },
  PRO: { label: 'PRO (3-5 employés)', color: 'purple', emoji: '👥' },
  ENTERPRISE: { label: 'ENTERPRISE (+5 employés)', color: 'gold', emoji: '🏢' },
  MARKETPLACE: { label: 'Marketplace pressing', color: 'green', emoji: '🧺' },
};

const InstallationRequestsPipeline: React.FC = () => {
  const { data: requests = [], isLoading } = useInstallationRequests();
  const updateStatus = useUpdateInstallationRequestStatus();
  const updateNotes = useUpdateInstallationRequestNotes();
  const cancelRequest = useCancelInstallationRequest();

  const [searchText, setSearchText] = useState('');
  const [forfaitFilter, setForfaitFilter] = useState<string>('all');
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [notesModalVisible, setNotesModalVisible] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<InstallationRequest | null>(null);
  const [notesForm] = Form.useForm();
  const [cancelForm] = Form.useForm();
  const [draggedCard, setDraggedCard] = useState<InstallationRequest | null>(null);

  // Filtrage des demandes
  const filteredRequests = useMemo(() => {
    return requests.filter((req) => {
      const matchesSearch = matchesSearchInFields(
        [req.companyName, req.responsibleName, req.location, req.whatsapp],
        searchText
      );
      
      const matchesForfait = forfaitFilter === 'all' || !req.forfait || req.forfait === forfaitFilter;

      return matchesSearch && matchesForfait;
    });
  }, [requests, searchText, forfaitFilter]);

  // Grouper les demandes par statut
  const requestsByStatus = useMemo(() => {
    const grouped: Record<InstallationRequestStatus, InstallationRequest[]> = {
      pending: [],
      contacted: [],
      installed: [],
      cancelled: [],
    };

    filteredRequests.forEach((req) => {
      grouped[req.status].push(req);
    });

    return grouped;
  }, [filteredRequests]);

  // Statistiques
  const stats = useMemo(() => {
    return {
      total: requests.length,
      pending: requests.filter((r) => r.status === 'pending').length,
      contacted: requests.filter((r) => r.status === 'contacted').length,
      installed: requests.filter((r) => r.status === 'installed').length,
      cancelled: requests.filter((r) => r.status === 'cancelled').length,
    };
  }, [requests]);

  // Handlers
  const handleViewDetails = (request: InstallationRequest) => {
    setSelectedRequest(request);
    setDetailsModalVisible(true);
  };

  const handleEditNotes = (request: InstallationRequest) => {
    setSelectedRequest(request);
    notesForm.setFieldsValue({ notes: request.notes || '' });
    setNotesModalVisible(true);
  };

  const handleCancelRequest = (request: InstallationRequest) => {
    setSelectedRequest(request);
    cancelForm.resetFields();
    setCancelModalVisible(true);
  };

  const handleStatusChange = async (requestId: string, newStatus: InstallationRequestStatus) => {
    await updateStatus.mutateAsync({ id: requestId, status: newStatus });
  };

  const handleSaveNotes = async () => {
    if (!selectedRequest) return;
    const values = await notesForm.validateFields();
    await updateNotes.mutateAsync({ id: selectedRequest.id, notes: values.notes });
    setNotesModalVisible(false);
    setSelectedRequest(null);
  };

  const handleConfirmCancel = async () => {
    if (!selectedRequest) return;
    const values = await cancelForm.validateFields();
    await cancelRequest.mutateAsync({ id: selectedRequest.id, cancelReason: values.cancelReason });
    setCancelModalVisible(false);
    setSelectedRequest(null);
  };

  // Drag and Drop handlers
  const handleDragStart = (request: InstallationRequest) => {
    setDraggedCard(request);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (targetStatus: InstallationRequestStatus) => {
    if (!draggedCard || draggedCard.status === targetStatus) {
      setDraggedCard(null);
      return;
    }

    await handleStatusChange(draggedCard.id, targetStatus);
    setDraggedCard(null);
  };

  // Export Excel
  const handleExportExcel = () => {
    const headers = ['Date', 'Entreprise', 'Responsable', 'WhatsApp', 'Email', 'Localisation', 'Forfait', 'Statut', 'Notes'];
    const rows = filteredRequests.map((req) => [
      dayjs(req.createdAt).format('DD/MM/YYYY HH:mm'),
      req.companyName,
      req.responsibleName,
      req.whatsapp,
      req.email || '',
      req.location,
      req.forfait || '',
      req.status,
      req.notes || '',
    ]);

    const csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `demandes_installation_${dayjs().format('YYYY-MM-DD')}.csv`;
    link.click();
  };

  // Rendu d'une card de demande
  const renderRequestCard = (request: InstallationRequest) => {
    const forfaitInfo = request.forfait ? forfaitConfig[request.forfait] : null;
    const menu = (
      <Menu>
        <Menu.Item key="view" icon={<EyeOutlined />} onClick={() => handleViewDetails(request)}>
          Voir détails
        </Menu.Item>
        <Menu.Item key="notes" icon={<EditOutlined />} onClick={() => handleEditNotes(request)}>
          Modifier notes
        </Menu.Item>
        {request.status !== 'cancelled' && (
          <>
            <Menu.Divider />
            <Menu.Item key="cancel" danger icon={<CloseCircleOutlined />} onClick={() => handleCancelRequest(request)}>
              Annuler
            </Menu.Item>
          </>
        )}
      </Menu>
    );

    return (
      <Card
        key={request.id}
        className="pipeline-request-card"
        size="small"
        draggable
        onDragStart={() => handleDragStart(request)}
        onClick={() => handleViewDetails(request)}
      >
        {/* Header */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '4px' }}>
                {request.companyName}
              </Text>
              {(request.requestType === 'marketplace_pressing' ||
                request.source === 'ramassage_marketplace' ||
                request.forfait === 'MARKETPLACE') && (
                <Tag color="green" style={{ marginBottom: 6, fontSize: 11 }}>
                  Marketplace pressing
                </Tag>
              )}
              <Space size={4}>
                <UserOutlined style={{ fontSize: '12px', color: '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {request.responsibleName}
                </Text>
              </Space>
            </div>
            <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
              <Button
                type="text"
                size="small"
                icon={<MoreOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
          </div>
        </div>

        {/* Contact */}
        <div style={{ marginBottom: '8px' }}>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Space size={4}>
              <WhatsAppOutlined style={{ fontSize: '12px', color: '#25D366' }} />
              <Text style={{ fontSize: '12px' }}>{request.whatsapp}</Text>
            </Space>
            {request.email && (
              <Space size={4}>
                <MailOutlined style={{ fontSize: '12px', color: '#1890ff' }} />
                <Text style={{ fontSize: '12px' }} ellipsis>{request.email}</Text>
              </Space>
            )}
            <Space size={4}>
              <EnvironmentOutlined style={{ fontSize: '12px', color: '#ff4d4f' }} />
              <Text style={{ fontSize: '12px' }} ellipsis>{request.location}</Text>
            </Space>
          </Space>
        </div>

        {/* Tags */}
        <div style={{ marginBottom: '8px' }}>
          <Space wrap size={4}>
            {forfaitInfo && (
              <Tag color={forfaitInfo.color} style={{ fontSize: '11px', margin: 0 }}>
                {forfaitInfo.emoji} {forfaitInfo.label}
              </Tag>
            )}
          </Space>
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '8px', marginTop: '8px' }}>
          <Space size={4} style={{ width: '100%', justifyContent: 'space-between' }}>
            <Tooltip title={dayjs(request.createdAt).format('DD/MM/YYYY à HH:mm')}>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {dayjs(request.createdAt).fromNow()}
              </Text>
            </Tooltip>
            {request.notes && (
              <Tooltip title={request.notes}>
                <FileTextOutlined style={{ fontSize: '12px', color: '#1890ff' }} />
              </Tooltip>
            )}
          </Space>
        </div>
      </Card>
    );
  };

  return (
    <div className="installation-requests-pipeline">
      {/* Header avec statistiques */}
      <div className="pipeline-header">
        <div className="pipeline-title-section">
          <Title level={3} style={{ margin: 0 }}>
            Pipeline d'installation
          </Title>
          <Text type="secondary">
            Gérez vos demandes d'installation par glisser-déposer
          </Text>
        </div>

        {/* Stats cards */}
        <div className="pipeline-stats">
          <Card className="stat-card-mini">
            <div className="stat-content-mini">
              <Text type="secondary" style={{ fontSize: '12px' }}>Total</Text>
              <Text strong style={{ fontSize: '20px', color: '#722ed1' }}>{stats.total}</Text>
            </div>
          </Card>
          <Card className="stat-card-mini">
            <div className="stat-content-mini">
              <Text type="secondary" style={{ fontSize: '12px' }}>Nouveau</Text>
              <Text strong style={{ fontSize: '20px', color: '#faad14' }}>{stats.pending}</Text>
            </div>
          </Card>
          <Card className="stat-card-mini">
            <div className="stat-content-mini">
              <Text type="secondary" style={{ fontSize: '12px' }}>Contacté</Text>
              <Text strong style={{ fontSize: '20px', color: '#1890ff' }}>{stats.contacted}</Text>
            </div>
          </Card>
          <Card className="stat-card-mini">
            <div className="stat-content-mini">
              <Text type="secondary" style={{ fontSize: '12px' }}>Installé</Text>
              <Text strong style={{ fontSize: '20px', color: '#52c41a' }}>{stats.installed}</Text>
            </div>
          </Card>
        </div>
      </div>

      {/* Filtres */}
      <Card className="filters-card" style={{ marginBottom: 20 }}>
        <Space size="middle" wrap style={{ width: '100%' }}>
          <Search
            placeholder="Rechercher une entreprise, responsable, localisation..."
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            prefix={<FilterOutlined />}
          />
          <Select
            value={forfaitFilter}
            onChange={setForfaitFilter}
            style={{ width: 200 }}
            options={[
              { value: 'all', label: 'Tous les forfaits' },
              { value: 'SOLO', label: '👤 SOLO' },
              { value: 'PRO', label: '👥 PRO' },
              { value: 'ENTERPRISE', label: '🏢 ENTERPRISE' },
              { value: 'MARKETPLACE', label: '🧺 Marketplace pressing' },
            ]}
          />
          <Button icon={<ExportOutlined />} onClick={handleExportExcel}>
            Exporter Excel
          </Button>
        </Space>
      </Card>

      {/* Pipeline Kanban */}
      <Spin spinning={isLoading} size="large">
        <div className="pipeline-board">
          {PIPELINE_COLUMNS.map((column) => (
            <div
              key={column.key}
              className="pipeline-column"
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(column.key)}
            >
              {/* Column Header */}
              <div className="pipeline-column-header" style={{ background: column.bgColor }}>
                <Space>
                  <span style={{ fontSize: '16px', color: column.color }}>{column.icon}</span>
                  <Text strong style={{ fontSize: '14px' }}>
                    {column.title}
                  </Text>
                  <Badge
                    count={requestsByStatus[column.key].length}
                    style={{ backgroundColor: column.color }}
                  />
                </Space>
                <Tooltip title={column.description}>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    {column.description}
                  </Text>
                </Tooltip>
              </div>

              {/* Column Content */}
              <div className="pipeline-column-content">
                {requestsByStatus[column.key].length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Aucune demande"
                    style={{ padding: '40px 20px' }}
                  />
                ) : (
                  requestsByStatus[column.key].map((request) => renderRequestCard(request))
                )}
              </div>
            </div>
          ))}
        </div>
      </Spin>

      {/* Modal de détails */}
      <Modal
        title="Détails de la demande"
        open={detailsModalVisible}
        onCancel={() => {
          setDetailsModalVisible(false);
          setSelectedRequest(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Fermer
          </Button>,
        ]}
        width={600}
      >
        {selectedRequest && (
          <div>
            {/* ... (même contenu que dans InstallationRequests.tsx) ... */}
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Text type="secondary">Entreprise</Text>
                <div><Text strong>{selectedRequest.companyName}</Text></div>
              </div>
              <div>
                <Text type="secondary">Responsable</Text>
                <div><Text strong>{selectedRequest.responsibleName}</Text></div>
              </div>
              <div>
                <Text type="secondary">WhatsApp</Text>
                <div><Text code copyable>{selectedRequest.whatsapp}</Text></div>
              </div>
              {selectedRequest.email && (
                <div>
                  <Text type="secondary">Email</Text>
                  <div><Text code copyable>{selectedRequest.email}</Text></div>
                </div>
              )}
              <div>
                <Text type="secondary">Localisation</Text>
                <div><Text>{selectedRequest.location}</Text></div>
              </div>
              {selectedRequest.forfait && (
                <div>
                  <Text type="secondary">Forfait</Text>
                  <div>
                    <Tag color={forfaitConfig[selectedRequest.forfait]?.color || 'default'}>
                      {forfaitConfig[selectedRequest.forfait]?.emoji} {forfaitConfig[selectedRequest.forfait]?.label || selectedRequest.forfait}
                    </Tag>
                  </div>
                </div>
              )}
              <div>
                <Text type="secondary">Source</Text>
                <div>
                  <Text>
                    {selectedRequest.requestType === 'marketplace_pressing' ||
                    selectedRequest.source === 'ramassage_marketplace'
                      ? 'Marketplace ramassage — candidature pressing'
                      : selectedRequest.source === 'landing_home' ||
                          selectedRequest.source === 'landing_lancement'
                        ? "Page d'accueil — démo"
                        : selectedRequest.source || '-'}
                  </Text>
                </div>
              </div>
              <div>
                <Text type="secondary">Date de création</Text>
                <div><Text>{dayjs(selectedRequest.createdAt).format('DD/MM/YYYY à HH:mm')}</Text></div>
              </div>
              {selectedRequest.notes && (
                <div>
                  <Text type="secondary">Notes internes</Text>
                  <div><Text>{selectedRequest.notes}</Text></div>
                </div>
              )}
            </Space>
          </div>
        )}
      </Modal>

      {/* Modal de notes */}
      <Modal
        title="Modifier les notes"
        open={notesModalVisible}
        onOk={handleSaveNotes}
        onCancel={() => {
          setNotesModalVisible(false);
          setSelectedRequest(null);
        }}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        <Form form={notesForm} layout="vertical">
          <Form.Item name="notes" label="Notes internes">
            <TextArea rows={4} placeholder="Ajoutez des notes internes sur cette demande..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal d'annulation */}
      <Modal
        title="Annuler la demande"
        open={cancelModalVisible}
        onOk={handleConfirmCancel}
        onCancel={() => {
          setCancelModalVisible(false);
          setSelectedRequest(null);
        }}
        okText="Annuler la demande"
        cancelText="Fermer"
        okButtonProps={{ danger: true }}
      >
        <Form form={cancelForm} layout="vertical">
          <Form.Item
            name="cancelReason"
            label="Raison de l'annulation"
            rules={[{ required: true, message: 'Veuillez indiquer la raison' }]}
          >
            <TextArea
              rows={4}
              placeholder="Expliquez pourquoi cette demande est annulée..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InstallationRequestsPipeline;
